import { useMutation, useQuery } from '@apollo/client';
import { Box, Button, Container, Paper, Typography, styled } from '@mui/material';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { PasswordStrengthMeterBox } from '~/components/PasswordStrengthMeterBox';
import { PasswordField } from '~/components/forms/password-field';
import { ButtonLink } from '~/components/link';
import { CreatePasswordDocument, SetPasswordDocument } from '~/generated/graphql';
import logo from '~/images/fc-logo.svg';
import { MIN_PASSWORD_STRENGTH, usePasswordStrength } from '~/lib/password';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  backgroundColor: theme.palette.primary.main,
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0, 2),
  padding: theme.spacing(6),
  border: '1px solid #E6E6E6',
  borderRadius: '2px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const Logo = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 1 40%',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '75%',
  padding: theme.spacing(0, 0, 1),
}));

const messageSx = {
  textTransform: 'uppercase' as const,
  color: 'text.primary',
  px: 0,
  pt: 0,
  pb: 2,
};

const Form = styled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
  textAlign: 'center',
}));

const FormInput = styled('div')(({ theme }) => ({
  margin: theme.spacing(1.5, 0),
}));

const HiddenEmail = styled('input')({
  display: 'none',
});

export const CreatePassword = () => {
  const { t } = useTranslation();

  const { token = '' } = useParams<{ token: string }>();

  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const { data } = useQuery(CreatePasswordDocument, { variables: { token } });
  const [setPassword, setPasswordState] = useMutation(SetPasswordDocument, {
    fetchPolicy: 'no-cache',
  });

  const passwordStrength = usePasswordStrength(newPassword);

  if (data?.currentUser != null) return <Navigate replace to="/" />;

  const success = setPasswordState.data?.setPassword?.success ?? false;

  return (
    <Root>
      <Helmet title={t('pages.auth.create_password.title')} />
      <Container maxWidth="sm">
        <FormPaper elevation={5}>
          <Logo>
            <img alt="FanConnect" src={logo} />
          </Logo>
          {!success || setPasswordState.loading ? (
            <>
              <Typography component="h1" variant="h5" sx={messageSx}>
                {t('pages.auth.create_password.title')}
              </Typography>
              {setPasswordState.error && (
                <Typography align="center" color="error" role="alert">
                  {t('pages.auth.create_password.failed', {
                    message: setPasswordState.error.message,
                  })}
                </Typography>
              )}
              <Form
                onSubmit={async (event) => {
                  event.preventDefault();
                  await setPassword({
                    variables: { password: newPassword, token },
                  }).catch((_) => {
                    // TODO: Do something appropriate here
                    alert('failed');
                  });
                }}
              >
                <HiddenEmail
                  type="text"
                  autoComplete="username"
                  value={data?.emailByToken ?? undefined}
                  readOnly
                />
                <FormInput>
                  <PasswordField
                    kind="new"
                    label={t('pages.auth.create_password.new_password')}
                    autoFocus
                    fullWidth
                    value={newPassword}
                    disabled={setPasswordState.loading}
                    onChange={(event) => {
                      setNewPassword(event.target.value);
                    }}
                  />
                </FormInput>
                <PasswordStrengthMeterBox strength={passwordStrength} />
                <FormInput>
                  <PasswordField
                    kind="new"
                    label={t('pages.auth.create_password.confirm_password')}
                    fullWidth
                    value={confirmation}
                    disabled={setPasswordState.loading}
                    onChange={(event) => {
                      setConfirmation(event.target.value);
                    }}
                  />
                </FormInput>
                {confirmation !== '' && confirmation !== newPassword && (
                  <p>{t('pages.auth.create_password.passwords_no_match')}</p>
                )}
                <FormInput>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    color="secondary"
                    size="large"
                    disabled={
                      !newPassword ||
                      !confirmation ||
                      (passwordStrength?.score ?? 0) < MIN_PASSWORD_STRENGTH ||
                      newPassword !== confirmation ||
                      setPasswordState.loading
                    }
                  >
                    {t('pages.auth.create_password.set_password')}
                  </Button>
                </FormInput>
              </Form>
            </>
          ) : setPasswordState.error?.networkError || data?.emailByToken == null ? (
            <>
              <Typography textAlign="center" marginY="8px" color="error" role="alert">
                {t('pages.auth.create_password.error')}
              </Typography>
              <FormInput>
                <Button
                  variant="contained"
                  fullWidth
                  color="secondary"
                  size="large"
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  {t('pages.auth.create_password.reload_page')}
                </Button>
              </FormInput>
            </>
          ) : (
            <>
              <Typography variant="subtitle1" textAlign="center" marginY="8px">
                {t('pages.auth.create_password.success')}
              </Typography>
              <FormInput>
                <ButtonLink variant="contained" color="secondary" size="large" to="/login">
                  {t('pages.auth.create_password.sign_in')}
                </ButtonLink>
              </FormInput>
            </>
          )}
        </FormPaper>
        <Box flex="1 1 40%" />
      </Container>
    </Root>
  );
};
